import {
  depositConflictSchema,
  inspectionSubmissionSchema,
  isVideoMediaType,
  newInspectionStep,
  newUuidV7,
  performedOnSchema,
  PROPOSAL_PAYLOAD_VERSION,
  SURVEY_V1_VERSION,
  SURVEY_VERSION,
  suggestRecommendations,
  upgradeSurvey,
} from '@ghp/domain'
import { ApiError, type ApiClient } from '@ghp/api-client'
import type {
  AppointmentContact,
  DepositConflictCode,
  FieldInspectionStep,
  InspectionSubmission,
  MediaUploadRequest,
  PassageNoticeReason,
  SurveyV1,
  SurveyV2,
  VocabularyEntry,
} from '@ghp/domain'

import {
  showFieldAppointment,
  updateRow,
  type DataDatabase,
  type FieldAppointmentEntry,
  type InspectionDraft,
  type InspectionStepEntry,
  type LocalMedium,
  type OutboxEntry,
  type OutboxState,
  type PassageNoticeEntry,
} from './data-db'
import { putSigned, setUploadProgress } from './direct-upload'
import { metPersonOf } from './met-person'

/** Le sous-ensemble injecté du client : ce que l'outbox pousse, rien d'autre. */
export type SyncApi = Pick<
  ApiClient['sync'],
  'submitInspection' | 'uploadMedia' | 'mediaUploadUrl'
> & {
  /** L'envoi direct au stockage (#418) ; `XMLHttpRequest` du navigateur si non injecté. */
  putSigned?: typeof putSigned
}

/**
 * Le dépôt d'un **avis de passage** — une tranche d'API à part, comme `PullApi` et `LogShipApi`.
 *
 * ⚠️ **Ce n'est pas le module `sync`**, et c'est le contrat qui le dit : la route vit sous
 * `/passage-notices`, servie par le module `inquiries` du serveur. Un avis n'est pas une
 * proposition à valider (D4) — c'est un **fait** que seule la tablette connaît, du même genre que
 * le journal de diagnostic qu'elle dépose déjà.
 */
export type PassageNoticeApi = Pick<ApiClient['passageNotices'], 'record'>

/**
 * La pose d'un **rendez-vous pris sur place** — la seule route du planning ouverte à la tablette
 * (`POST /planning/field-appointments`, #268).
 */
export type FieldAppointmentApi = Pick<ApiClient['planning'], 'recordFieldAppointment'>

/** Le dépôt de l'étape d'un contrôle (`POST /inspection-steps`, M42). */
export interface InspectionStepApi {
  recordInspectionStep: ApiClient['inspectionSteps']['record']
}

export interface FlushOptions {
  /**
   * Une reconnexion ou un geste de l'agent : la passe rejoue **tout**, y compris les envois
   * dont la reprise automatique est encore programmée plus tard (`nextAttemptAt`).
   */
  forced?: boolean
  /**
   * Un envoi a pris un `401` avec ce jeton : le détenteur de la session doit le savoir —
   * c'est lui qui a le refresh, la file n'a que le jeton qu'on lui prête.
   */
  onUnauthorized?: (accessToken: string) => void
}

/**
 * Les envois qu'une passe reprend d'elle-même. Un `sent` est fini ; un `rejected` attend une
 * décision humaine — le rejouer à l'identique ne rendrait que le même refus. Un `failed`
 * (refus définitif du serveur, 4xx) ne se rejoue plus tout seul non plus : le même envoi
 * rendrait le même refus à chaque minute, en silence. Ni l'un ni l'autre n'est terminal :
 * `retrySubmission` rouvre la fiche de l'un ou de l'autre, `rearmSubmission` remet un échec en jeu.
 */
const REPLAYABLE: OutboxState[] = ['pending', 'sending']

/**
 * Cadence de reprise d'un incident **transitoire** (réseau, `5xx`, `429`) : les deux premières
 * tentatives repartent au tour suivant, puis l'attente grandit d'une minute par tentative,
 * plafonnée. Une reconnexion ou un geste de l'agent (`forced`) passe outre — c'est le moment
 * où rejouer a le plus de chances d'aboutir.
 */
function retryDelayMs(attempts: number): number {
  if (attempts < 3) return 0
  return Math.min(attempts, 10) * 60_000
}

/**
 * Les `4xx` transitoires **par nature** : `429` par contrat, `408` quand un proxy coupe un
 * upload média lent — le lien 4G d'un chantier.
 */
const TRANSIENT_STATUSES = new Set([408, 429])

/**
 * Le `404` ne tient pas dans la liste ci-dessus : il est **ambigu**. Un seul le rend définitif —
 * celui que l'API lève au **dépôt** d'une demande disparue (`SyncService.deposit` — « aucune
 * demande de contrôle … ») : là, l'agent doit être renvoyé vers le bureau. Deux conditions,
 * toutes deux nécessaires :
 *
 * - **au dépôt**, car aucune route de média ne rend de `404` métier (`PUT /sync/media/:id` ne
 *   sert que `400`, `401`, `403`, `413`, `415`, `429`, `500`) : un `404` pendant une montée est
 *   forcément un défaut de routage, donc transitoire ;
 * - **avec un corps**, car le transport ne pose `body` que sur une réponse JSON lisible — la
 *   page d'erreur de nginx ou du WAF pendant un déploiement n'en est pas une.
 *
 * ⚠️ Le corps ne dit pas *qui* a répondu, seulement que la réponse était du JSON. Le `404` du
 * routeur de Nest — chemin d'API inconnu, bundle de tablette plus vieux que l'API — en porte un
 * et se lit ici comme un refus : l'envoi se gare avec « Prévenez le bureau » au lieu de tourner
 * en boucle. Un signalement plutôt qu'une boucle silencieuse (M21), mais il faut alors
 * ré-armer les envois garés une fois l'API réparée. La sortie propre est un `409` à code
 * machine pour la demande disparue, côté API — hors périmètre de cette PR.
 */
function isTransient(error: ApiError, atDeposit: boolean): boolean {
  if (TRANSIENT_STATUSES.has(error.status)) return true

  return error.status === 404 && !(atDeposit && error.body !== undefined)
}

/**
 * Ouvre — ou rouvre — la saisie d'un contrôle.
 *
 * **Idempotent par nécessité** : l'agent revient sur sa fiche, l'écran se remonte, la tablette
 * redémarre. Une seconde création rendrait un second `idempotencyKey`, donc un doublon côté
 * serveur au premier rejeu.
 *
 * ⚠️ **Pas de brouillon sur une demande inconnue** — l'invariant vit ici, pas seulement chez
 * l'appelant : une fiche rattachée à un identifiant hors de la tournée de l'équipe serait
 * saisissable et transmissible, pour un dépôt que le serveur refusera toujours. Une saisie **déjà
 * commencée** passe d'abord et sans condition (M21) : la demande peut avoir quitté le cache,
 * le relevé de l'agent, lui, n'existe que là.
 *
 * ⚠️ **Une saisie déjà là est rendue telle quelle, sans réécriture.** La base ouverte est celle
 * de l'équipe de la session (D22 pt.4) : ce qui s'y trouve est à elle, il n'y a rien à
 * rattacher à personne. Ce `put` de reprise existait pour reposer l'estampille d'équipe sur une
 * fiche de l'arriéré — une écriture par ouverture de fiche, pour un champ qui n'existe plus.
 */
export async function createDraft(db: DataDatabase, inquiryId: string): Promise<InspectionDraft> {
  return db.transaction('rw', [db.drafts, db.inquiries, db.inspectionSteps], async () => {
    const existing = await db.drafts.get(inquiryId)
    if (existing) return existing

    // ⚠️ La demande est lue dans la base de **l'équipe de la session** : sur une demande confiée
    // à une autre équipe, la tablette se conduit exactement comme sur une demande qu'elle n'a
    // pas — « Contrôle inconnu », dont l'issue est de redescendre la tournée. Une équipe ne
    // saisit pas sur la demande d'une autre, même en connaissant son identifiant.
    const inquiry = await db.inquiries.get(inquiryId)
    if (inquiry === undefined) {
      throw new Error(
        `Pas de brouillon pour le contrôle ${inquiryId} : la demande n'est pas dans la tournée de la tablette.`,
      )
    }

    // Une contre-visite (#321) repart du relevé et des anomalies du contrôle qu'elle suit : l'agent
    // retire ce qui a été corrigé. **Le schéma compris, éléments recopiés** (#675) : l'agent le
    // modifie, et le schéma transmis — donc celui du rapport — montre l'installation entière, pas
    // seulement ce qui a été ajouté. C'est une copie : Dexie range un clone, l'origine servie par la
    // tournée ne bouge pas. Ses photos et pièces sont déjà dans `media`, rangées à la descente
    // (`storeCarriedMedia`). Rien de ce qui appartient au passage d'origine — personne rencontrée,
    // signature, jour — ne suit.
    const source = inquiry.followUpSource ?? null
    const draft: InspectionDraft = {
      inquiryId,
      idempotencyKey: newUuidV7(),
      survey: source === null ? {} : source.survey,
      surveyVersion: SURVEY_VERSION,
      anomalies: source === null ? [] : source.anomalies,
      metPerson: null,
      certifiedWaterPoints: false,
      certifiedNoSepticTank: false,
      consentedDataRetention: false,
      signatureMediaId: null,
      signedAt: null,
      // Vide tant que l'agent n'a rien déclaré : ouvrir une fiche n'est pas passer sur place —
      // une tournée se prépare la veille —, et un jour posé d'office ferait dire à la tournée
      // « Fait le … » sur un contrôle que personne n'a réalisé. #115 demande de **montrer**
      // l'écart entre le rendez-vous et le passage, pas d'en fabriquer un. L'absence se voit :
      // la carte, la liste d'avertissements et le bureau la nomment, et rien ne bloque (M21).
      performedOn: null,
      lat: null,
      lon: null,
      notes: null,
      lockedAt: null,
      updatedAt: new Date().toISOString(),
    }

    await db.drafts.put(draft)
    // « Commencé » (M42) : une fois, à la naissance du brouillon — c'est l'ouverture de la fiche.
    await recordInspectionStep(db, inquiryId, 'started')

    return draft
  })
}

/**
 * Transmettre : verrouiller la fiche et la mettre en file. **Le réseau n'entre pas ici** — le
 * geste de l'agent aboutit qu'il y ait du réseau ou non, `flushOutbox` s'occupe du reste.
 *
 * Tout se fait dans une seule transaction : une fiche verrouillée sans envoi en file serait une
 * saisie perdue, un envoi en file sans verrou laisserait l'agent modifier ce qui est parti.
 *
 * **Le verrou est le seul juge d'une seconde mise en file** : une fiche verrouillée est déjà
 * partie, et l'état de son envoi ne change rien à cela — on rend l'entrée existante plutôt que
 * d'empiler une seconde enveloppe sous la même clé. Un refus se reprend par `retrySubmission`,
 * qui déverrouille la fiche ; ce déverrouillage est la seule porte vers une enveloppe neuve.
 */
export async function enqueueSubmission(
  db: DataDatabase,
  inquiryId: string,
  now: () => Date,
  vocabulary: readonly VocabularyEntry[] = [],
): Promise<OutboxEntry> {
  return db.transaction('rw', [db.drafts, db.media, db.outbox, db.inquiries], async () => {
    const draft = await db.drafts.get(inquiryId)
    if (!draft) throw new Error(`Aucune fiche à transmettre pour le contrôle ${inquiryId}.`)

    const queued = await latestEntry(db, inquiryId)
    if (draft.lockedAt && queued) return queued

    const media = await db.media.where('inquiryId').equals(inquiryId).sortBy('id')
    const cached = await db.inquiries.get(inquiryId)
    // Le contact de la demande signe quand personne d'autre n'est sur place (#555). `?? null` :
    // une demande absente du cache, ou mise en cache avant `contact`, laisse la carte seule.
    const contact = cached?.contact ?? null

    const submittedAt = now().toISOString()
    // Un contrôle libre part avec la saisie de l'agent : le serveur la range sans demande (M41).
    const free = cached?.freeInspection
    const submission = checked({
      ...buildSubmission(draft, media, submittedAt, vocabulary, contact),
      ...(free === undefined
        ? {}
        : {
            freeInspection: {
              address: free.address,
              clientName: free.clientName,
              occupantName: free.occupantName,
              purpose: free.purpose,
            },
          }),
    })

    const entry: OutboxEntry = {
      id: newUuidV7(),
      inquiryId,
      submission,
      state: 'pending',
      attempts: 0,
      lastError: null,
      conflictCode: null,
      createdAt: submittedAt,
      sentAt: null,
    }

    // Transmettre verrouille aussi une signature que l'agent n'a pas validée : sans cela, une
    // fiche renvoyée par le bureau la rouvrirait à l'effacement (#352).
    const signatureLock =
      draft.signedAt !== null && draft.signatureLockedAt == null
        ? { signatureLockedAt: submittedAt }
        : {}
    await updateRow(db.drafts, inquiryId, {
      lockedAt: submittedAt,
      ...signatureLock,
      updatedAt: submittedAt,
    })

    await db.outbox.put(entry)

    return entry
  })
}

/**
 * Reprendre un envoi refusé : la fiche redevient modifiable, et une transmission neuve peut
 * repartir.
 *
 * **Un `409` veut dire « rien n'est enregistré »** (`depositConflictSchema`) : le relevé n'existe
 * alors que sur cette tablette. En faire un état terminal l'y condamnerait — et le message du
 * serveur dit l'inverse, jusqu'à « elle ne doit pas être retirée de la file ». L'entrée refusée
 * **reste en file** avec le motif reçu : c'est la seule trace de ce que le bureau a répondu (M21).
 *
 * ⚠️ La clé d'idempotence ne se régénère que sur `idempotency_key_reused` : là, elle porte déjà
 * une **autre** demande côté serveur, et la rejouer rendrait éternellement le même refus. Sur
 * tout autre motif elle est conservée — rien n'a été rangé sous elle, et la garder fait qu'une
 * reprise interrompue en pleine transmission ne produira pas de doublon.
 *
 * Un envoi **en échec** (`failed`, un `4xx` définitif) se rouvre aussi : le serveur n'a rien
 * rangé non plus, et quand c'est le contenu qui est refusé — un relevé au-delà de la limite du
 * dépôt —, « Réessayer l'envoi » rejouerait le même corps. Sans cette porte, la fiche restait
 * verrouillée et intransmissible, et le bureau, qui n'a rien reçu, ne pouvait rien renvoyer (#246).
 */
export async function retrySubmission(
  db: DataDatabase,
  inquiryId: string,
): Promise<InspectionDraft> {
  return db.transaction('rw', [db.drafts, db.outbox], async () => {
    const draft = await db.drafts.get(inquiryId)
    if (!draft) throw new Error(`Aucune fiche à reprendre pour le contrôle ${inquiryId}.`)

    const last = await latestEntry(db, inquiryId)
    if (last?.state !== 'rejected' && last?.state !== 'failed') {
      throw new Error(
        `Aucun envoi refusé à reprendre pour le contrôle ${inquiryId} : il n’y a rien à rouvrir.`,
      )
    }

    return unlockDraft(
      db,
      inquiryId,
      last.conflictCode === 'idempotency_key_reused' ? newUuidV7() : draft.idempotencyKey,
    )
  })
}

/**
 * Reprendre un contrôle que **le bureau a renvoyé au terrain** : la fiche redevient modifiable,
 * et la correction repart sous une clé neuve (#228).
 *
 * C'est l'autre moitié de la boucle validation → renvoi → correction → retransmission. Le
 * renvoi ramène la demande dans la tournée et la fiche porte son motif (#169, #181) ; sans cette
 * sortie, l'agent lisait ce qu'on lui demandait de corriger sur un écran en consultation seule,
 * et n'avait d'autre issue que de tout ressaisir ou d'appeler le bureau.
 *
 * ⚠️ **La clé d'idempotence se retire toujours ici**, et c'est ce qui distingue ce chemin de
 * {@link retrySubmission} : l'envoi d'origine a été **accepté**, le serveur le porte en base sous
 * cette clé. La garder ferait reconnaître un rejeu et **rejouer la réponse d'origine** — le dépôt
 * répondrait `200`, la file classerait l'envoi « parti », et la correction serait avalée sans
 * qu'aucun écran n'échoue, ni sur la tablette ni au bureau, qui recevrait deux fois la version
 * non corrigée. Une clé neuve donne une **seconde proposition**, que le bureau lit en tête de la
 * fiche de validation : c'est exactement ce que D4 décrit — le terrain propose, le bureau
 * tranche, et rien n'est écrasé.
 *
 * ⚠️ **Le dépôt d'origine reste en file, en `sent`** : il est parti, et c'est vrai. Rien ne le
 * réécrit ni ne le supprime — le journal des notifications le montre repris par un envoi plus
 * récent (M21 : on ajoute, on n'efface pas).
 */
export async function reopenReturned(
  db: DataDatabase,
  inquiryId: string,
): Promise<InspectionDraft> {
  return db.transaction('rw', [db.drafts, db.inquiries, db.outbox], async () => {
    const draft = await db.drafts.get(inquiryId)
    if (!draft) throw new Error(`Aucune fiche à reprendre pour le contrôle ${inquiryId}.`)

    // Les deux faits, ensemble : un dépôt **accepté** (`sent`, le seul état qui dise que le
    // serveur l'a rangé) et une descente postérieure à lui. Sans le second, cette porte
    // déverrouillerait n'importe quel contrôle transmis — l'agent reprendrait une saisie que le
    // bureau est en train de valider, et lui en enverrait une seconde qu'il n'a pas demandée.
    const last = await latestEntry(db, inquiryId)
    const inquiry = await db.inquiries.get(inquiryId)

    if (
      last?.state !== 'sent' ||
      !returnedToField({ servedAt: inquiry?.servedAt, sentAt: last.sentAt })
    ) {
      throw new Error(
        `Le bureau n’a pas renvoyé le contrôle ${inquiryId} : il n’y a rien à reprendre. ` +
          'Synchronisez la tablette, et prévenez le bureau si le renvoi n’apparaît pas.',
      )
    }

    return unlockDraft(db, inquiryId, newUuidV7())
  })
}

/**
 * Le bureau a-t-il **rendu** ce contrôle à l'équipe depuis qu'elle l'a déposé ?
 *
 * Le statut ne peut pas répondre : la tournée servie ne contient que `assigned` et
 * `in_progress`, donc une demande rouverte redescend avec le statut qu'elle avait avant le
 * dépôt. Ce qui change, c'est **la date à laquelle le serveur l'a servie** : il cesse de la
 * servir tant qu'elle est `submitted`, et la ressert le jour où le bureau la renvoie. Une
 * descente postérieure au dépôt accepté est donc, à elle seule, la preuve du renvoi.
 *
 * ⚠️ **Une seule définition pour deux usages**, et ils ne pardonnent pas de diverger : l'écran
 * en tire l'état de tournée qu'il affiche (`features/inspections/lib/list-filters.ts`), la
 * reprise ci-dessus en tire le droit de **lever le verrou**. Deux lectures du même fait, c'est
 * une fiche qui annonce un renvoi sans laisser le reprendre, ou l'inverse.
 *
 * ⚠️ Deux `Date#toISOString()` se comparent **comme des chaînes** : même longueur, même
 * fuseau `Z`, l'ordre lexicographique est l'ordre chronologique. Les reparser ne changerait
 * rien au résultat et coûterait deux objets par ligne et par rendu.
 *
 * Sans l'un des deux horodatages, on ne conclut pas au renvoi : `servedAt` manque aux lignes
 * descendues avant qu'il existe, et une descente d'alors ne prouve rien. Le doute laisse la
 * ligne où elle est — et la fiche verrouillée, ce qu'une synchro suffit à lever.
 */
export function returnedToField(deposit: {
  /** Quand la tournée a servi cette demande pour la dernière fois. */
  servedAt: string | undefined
  /** Quand le serveur a **accepté** le dépôt, `null` s'il ne l'a pas encore fait. */
  sentAt: string | null
}): boolean {
  if (deposit.servedAt === undefined || deposit.sentAt === null) return false

  return deposit.servedAt > deposit.sentAt
}

/**
 * Déverrouiller une fiche déjà transmise, sous la clé d'envoi que l'appelant a choisie — c'est
 * le seul geste commun à la reprise d'un refus et à celle d'un renvoi, et le seul endroit d'où
 * une fiche ressort modifiable.
 *
 * Une modification, pas un `put` : seuls les champs de la reprise bougent, et une fiche que
 * l'agent aurait complétée entre-temps ne repart pas en arrière.
 *
 * ⚠️ **Relu, pas reconstruit.** C'est cette fiche que l'éditeur peint, et la peindre
 * déverrouillée sur la foi de ce qu'on a *voulu* écrire enferme l'agent : il corrige,
 * retransmet, et `enqueueSubmission` voit un verrou toujours posé — il lui rend l'ancien envoi.
 * Rien ne repart jamais, et aucun message n'explique pourquoi. Un `{ ...draft, ...reopened }` ne
 * peut pas se tromper *tant que* l'écriture lève au lieu de rendre un compte de lignes touchées
 * (`updateRow`) ; ce contrôle-là ne dépend d'aucune couche en dessous.
 */
async function unlockDraft(
  db: DataDatabase,
  inquiryId: string,
  idempotencyKey: string,
): Promise<InspectionDraft> {
  const reopened = { idempotencyKey, lockedAt: null, updatedAt: new Date().toISOString() }

  await updateRow(db.drafts, inquiryId, reopened)

  const reflected = await db.drafts.get(inquiryId)

  if (reflected?.lockedAt !== null || reflected.idempotencyKey !== reopened.idempotencyKey) {
    throw new Error(
      `La reprise du contrôle ${inquiryId} n’a pas été enregistrée : la fiche est restée telle ` +
        'quelle. À retenter — et si cela persiste, prévenez le bureau avant de ressaisir.',
    )
  }

  return reflected
}

/**
 * Le geste « Réessayer l'envoi » : remettre le dernier envoi du contrôle en jeu **tout de
 * suite** — un refus définitif (`failed`) redevient rejouable, une reprise programmée plus
 * tard (`nextAttemptAt`) saute. Sans effet sur un envoi rangé (`sent`) ou refusé par contrat
 * (`rejected`, dont la sortie est `retrySubmission`). Rien n'est supprimé : l'enveloppe et
 * son motif restent, seule la prochaine passe change (M21).
 */
export async function rearmSubmission(db: DataDatabase, inquiryId: string): Promise<void> {
  const last = await latestEntry(db, inquiryId)
  if (!last || last.state === 'sent' || last.state === 'rejected') return

  await updateRow(db.outbox, last.id, { state: 'pending', nextAttemptAt: null, tokenRefusals: 0 })
}

/**
 * La passe en cours, **une par base de tournée** — donc une par équipe (D22 pt.4).
 *
 * ⚠️ Une oscillation de réseau déclenche un appel, et cinq oscillations en déclenchent cinq :
 * autant de passes concurrentes remonteraient autant de fois les mêmes octets sur la 4G d'un
 * chantier. Un appel arrivé pendant une passe rejoint celle en cours et lui demande **un tour de
 * plus** — la file est relue, donc ce qui a été mis en file entre-temps part quand même.
 *
 * ⚠️ **Une par base, et pas une pour le module** : sur une tablette qui change d'équipe sans
 * rechargement — le scénario même que le cloisonnement adresse — un appel de l'équipe entrante
 * rejoindrait la passe de la sortante, dont la file est une autre file, dans une autre base. Il
 * croirait sa file vidée alors qu'elle n'aurait pas même été lue.
 *
 * ⚠️ Deux passes de deux bases ne peuvent plus lire la **même** entrée : chaque envoi n'existe
 * que dans une base, et une base n'est ouverte que par l'équipe qui la porte.
 *
 * ⚠️ **Ce que cette carte ne garantit pas** : elle vit dans le module, donc dans **un** onglet.
 * La PWA ouverte deux fois sur la même base viderait la même file en parallèle — les mêmes
 * octets montés deux fois, et un échec réseau de l'une réécrivant en `pending` l'entrée que
 * l'autre venait de classer `sent`. C'est {@link oneSenderAtATime} qui l'empêche, pour toute
 * l'origine.
 */
const passes = new Map<string, OutboxPass>()

/**
 * Le verrou d'origine sous lequel une passe vide la file — un seul, quelle que soit la base.
 *
 * Un par **origine** plutôt qu'un par base : deux équipes ne vident jamais leurs files en même
 * temps sur une tablette qui n'en connaît qu'une à la fois, et un verrou unique ne se trompe pas
 * de nom.
 */
export const OUTBOX_LOCK_NAME = 'ghp-terrain:outbox'

/** Le repli quand `navigator.locks` manque : la passe se sérialise dans cet onglet seulement. */
let localTurn: Promise<unknown> = Promise.resolve()

/**
 * **Une seule passe d'envoi à la fois pour l'origine**, tous onglets confondus — par
 * `navigator.locks`, que le navigateur tient pour toutes les pages de l'origine et relâche quand
 * l'une se ferme.
 *
 * Sans l'API (jsdom, un navigateur trop ancien), le repli sérialise les passes **de ce module**,
 * ce que le planificateur faisait déjà (`scheduler.ts`, drapeau `running`) ; deux onglets n'y sont
 * alors plus protégés l'un de l'autre.
 *
 * Une passe arrivée pendant celle d'un autre onglet **attend son tour**, elle ne s'en va pas :
 * c'est peut-être dans cet onglet que l'agent vient de transmettre, et son envoi doit partir sans
 * attendre le minuteur. Elle ne relit la file qu'une fois le verrou obtenu, donc ne remonte rien
 * de ce que l'autre a déjà envoyé.
 */
function oneSenderAtATime(body: () => Promise<void>): Promise<void> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(OUTBOX_LOCK_NAME, body)
  }

  const turn = localTurn.then(body)
  localTurn = turn.catch(() => undefined)

  return turn
}

interface OutboxPass {
  /** Ce que rend tout appel qui rejoint cette passe. */
  done: Promise<void>
  onePassMore: boolean
  oneForcedMore: boolean
}

/**
 * Vide la file. **Ne rejette jamais** : l'appelant enchaîne sur la descente des tournées, et une
 * entrée qui tourne mal ne doit pas lui coûter son référentiel.
 */
export function flushOutbox(
  db: DataDatabase,
  api: SyncApi & PassageNoticeApi & FieldAppointmentApi & InspectionStepApi,
  getToken: () => string | null,
  options: FlushOptions = {},
): Promise<void> {
  // Le nom de la base **est** l'identité de la file : `ghp-terrain-data-<équipe>`. Aucune base
  // ouverte — session pas encore lue — et l'appelant n'appelle pas : il n'y a pas de file.
  const queue = db.name

  const running = passes.get(queue)

  if (running) {
    running.onePassMore = true
    running.oneForcedMore = running.oneForcedMore || options.forced === true

    return running.done
  }

  const pass: OutboxPass = { done: Promise.resolve(), onePassMore: false, oneForcedMore: false }
  passes.set(queue, pass)
  pass.done = oneSenderAtATime(() => drainOutbox(db, api, getToken, options, pass)).finally(() => {
    passes.delete(queue)
  })

  return pass.done
}

/** L'état de la transmission d'un contrôle, ou `null` si personne ne l'a encore transmis. */
export function outboxStateOf(entries: OutboxEntry[], inquiryId: string): OutboxState | null {
  return outboxEntryOf(entries, inquiryId)?.state ?? null
}

/**
 * La dernière entrée de la file pour un contrôle : celle dont l'état — et le motif d'un
 * refus (`lastError`, M21) — se montrent à l'agent.
 */
export function outboxEntryOf(entries: OutboxEntry[], inquiryId: string): OutboxEntry | null {
  return latestOf(entries.filter((entry) => entry.inquiryId === inquiryId))
}

/**
 * La dernière entrée de **chaque** contrôle, en une passe.
 *
 * Ce que {@link outboxEntryOf} coûte pour une liste : rien ne supprime jamais une ligne de la
 * file, donc la table porte tous les envois jamais faits par la tablette. Interrogée par
 * demande, elle serait rebalayée — et retriée — autant de fois qu'il y a de lignes à l'écran,
 * à chaque montée de média. Une carte construite une fois, lue par ligne, est linéaire.
 */
export function latestSubmissions(entries: readonly OutboxEntry[]): Map<string, OutboxEntry> {
  const latest = new Map<string, OutboxEntry>()

  for (const entry of entries) {
    const known = latest.get(entry.inquiryId)
    if (known === undefined || byTransmissionOrder(known, entry) < 0) {
      latest.set(entry.inquiryId, entry)
    }
  }

  return latest
}

async function drainOutbox(
  db: DataDatabase,
  api: SyncApi & PassageNoticeApi & FieldAppointmentApi & InspectionStepApi,
  getToken: () => string | null,
  options: FlushOptions,
  pass: OutboxPass,
): Promise<void> {
  let forced = options.forced === true

  await releaseInterruptedSends(db)

  do {
    pass.onePassMore = false
    // Les étapes **d'abord** : une requête de quelques octets, que le bureau attend pour suivre la
    // journée. Derrière une tournée de vidéos, « Commencé » arriverait après « Transmis ».
    await sendQueuedSteps(db, api, getToken, { ...options, forced })
    await sendQueued(db, api, getToken, { ...options, forced })
    // Les avis **après** les contrôles, et jamais à leur place : un avis est trois champs qui
    // partent en une requête, un dépôt peut être une tournée de vidéos. Les faire passer devant
    // retarderait le travail de la journée derrière un constat de deux secondes ; les faire
    // partager la même boucle obligerait chaque lecteur de la file à discriminer.
    await sendQueuedNotices(db, api, getToken, { ...options, forced })
    await sendQueuedAppointments(db, api, getToken, { ...options, forced })
    forced = pass.oneForcedMore
    pass.oneForcedMore = false
  } while (pass.onePassMore)
}

/**
 * Ce qu'une passe **interrompue** a laissé `sending` — la tablette éteinte en pleine montée,
 * l'onglet fermé — redevient `pending`.
 *
 * `sending` dit « une tentative est en cours » (`data-db.ts`), et l'écran le lit ainsi : « Envoi… »,
 * sans « Réessayer l'envoi ». Laissé tel quel, il le disait pendant toute une coupure, ou des jours
 * sur une session refusée, puisqu'une passe sans jeton s'arrête avant de toucher la file.
 *
 * ⚠️ **Sûr parce que la passe tient le verrou d'origine** ({@link oneSenderAtATime}) : aucune
 * autre passe ne tourne, dans cet onglet ni dans un autre, donc un `sending` trouvé ici n'est
 * porté par personne. Sans l'API de verrous, le repli ne couvre que cet onglet.
 *
 * Rien d'autre ne change : ni `attempts`, ni la reprise programmée, ni le motif. Le rejeu reste
 * inoffensif (clé d'idempotence du contrôle, identifiant du média — D4).
 */
async function releaseInterruptedSends(db: DataDatabase): Promise<void> {
  try {
    await db.outbox.where('state').equals('sending').modify({ state: 'pending' })
    await db.passageNotices.where('state').equals('sending').modify({ state: 'pending' })
    await db.appointments.where('state').equals('sending').modify({ state: 'pending' })
    await db.inspectionSteps.where('state').equals('sending').modify({ state: 'pending' })
  } catch (error) {
    // Une base pleine ou illisible : la passe continue, et l'entrée reste rejouable telle quelle.
    console.error('[sync] envois interrompus non relâchés', error)
  }
}

/**
 * Une passe : les envois rejouables, **dans l'ordre où l'agent les a transmis**.
 *
 * ⚠️ **L'ordre est explicite, jamais celui du store** : `createdAt` puis `id` (un uuid v7, donc
 * croissant dans le temps — M7). S'en remettre à l'itération d'IndexedDB, c'est laisser un
 * contrôle arriver avant celui qu'il suit.
 *
 * Une entrée restée `sending` est reprise : c'est ce que laisse une tablette éteinte en pleine
 * tentative. Le rejeu est inoffensif — la clé d'idempotence du contrôle et l'identifiant du
 * média couvrent exactement ce cas (D4).
 */
async function sendQueued(
  db: DataDatabase,
  api: SyncApi,
  getToken: () => string | null,
  options: FlushOptions,
): Promise<void> {
  let entries: OutboxEntry[]

  try {
    entries = await db.outbox.where('state').anyOf(REPLAYABLE).toArray()
  } catch (error) {
    // Base illisible — évincée, ou fermée sous nos pieds par une mise à jour. Il n'y a nulle part
    // où consigner quoi que ce soit : on renonce à la passe sans faire tomber l'appelant.
    console.error('[sync] file d’envoi illisible, passe abandonnée', error)
    return
  }

  entries.sort(byTransmissionOrder)

  for (const entry of entries) {
    // Une reprise programmée plus tard ne se rejoue pas à chaque minute — sauf passe forcée
    // (reconnexion, geste de l'agent), où attendre n'aurait pas de sens.
    if (
      !options.forced &&
      entry.nextAttemptAt != null &&
      Date.parse(entry.nextAttemptAt) > Date.now()
    ) {
      continue
    }

    // Relu **à chaque entrée** : le jeton d'accès vaut 15 minutes (M23), une file de vidéos dure
    // plus longtemps. Lu une seule fois en tête de passe, il expirerait au milieu et enverrait
    // tout le reste de la tournée en échec.
    const accessToken = getToken()

    // Session dégradée : rien ne part, rien n'est perdu, et les entrées restantes restent
    // rejouables. Le retour du réseau rappellera.
    if (!accessToken) return

    try {
      await sendEntry(db, api, accessToken, entry, options)
    } catch (error) {
      // Une écriture locale qui échoue — le quota saturé par les vidéos en attente — n'emporte
      // pas la passe : cette entrée reste rejouable, les suivantes gardent leur chance.
      console.error(`[sync] envoi ${entry.id} abandonné`, error)
    }
  }
}

/**
 * Une transmission, **reprise là où elle en était** : les médias déjà montés sont sautés, et le
 * dépôt ne part qu'une fois tous les octets en place. La fiche ne référence que des médias que
 * le serveur possède déjà.
 *
 * ⚠️ Le passage à `sending` est **dans** le `try` : c'est une écriture, donc un candidat au
 * `QuotaExceededError`, et le laisser dehors faisait sauter la passe entière sur la première
 * entrée d'une tablette pleine.
 */
async function sendEntry(
  db: DataDatabase,
  api: SyncApi,
  accessToken: string,
  entry: OutboxEntry,
  options: FlushOptions,
): Promise<void> {
  // Où l'échec est survenu, pas seulement lequel : les montées de médias et le dépôt partagent
  // ce `catch`, et le `404` ne se lit pas de la même façon des deux côtés (voir `isTransient`).
  let atDeposit = false

  try {
    await updateRow(db.outbox, entry.id, { state: 'sending' })

    const { present, missing } = await referencedMedia(db, entry.submission)

    if (missing.length > 0) {
      // Rien n'est monté et rien n'est déposé : déposer une fiche dont une pièce a disparu
      // laisserait le bureau valider une photo fantôme, des jours plus tard et sans recours.
      // La reprise, elle, rend la fiche modifiable — l'agent peut refaire la prise de vue.
      await updateRow(db.outbox, entry.id, {
        state: 'rejected',
        conflictCode: null,
        attempts: entry.attempts + 1,
        lastError:
          `Des pièces de cette fiche ne sont plus sur la tablette (${missing.join(', ')}) : ` +
          'rien n’a été déposé. Reprendre la saisie pour les refaire.',
      })
      return
    }

    for (const medium of present.filter((candidate) => !candidate.uploadedAt)) {
      await uploadMedium(api, accessToken, medium)
      await updateRow(db.media, medium.id, { uploadedAt: new Date().toISOString() })
    }

    atDeposit = true
    await api.submitInspection(accessToken, entry.submission)

    await markSent(db, entry)
  } catch (error) {
    // **Un `409` signifie toujours « rien n'est enregistré »** — l'invariant du contrat, porté
    // par `@ghp/domain` et non recopié ici. L'entrée ne se classe donc jamais transmise : elle
    // reste visible avec le motif du serveur, ne se rejoue plus d'elle-même, et `retrySubmission`
    // la remet en jeu quand l'humain a tranché (M21).
    if (error instanceof ApiError && error.status === 409) {
      const conflict = readDepositConflict(error.body)

      await updateRow(db.outbox, entry.id, {
        state: 'rejected',
        conflictCode: conflict.code,
        attempts: entry.attempts + 1,
        lastError: conflict.message,
      })
      return
    }

    // **Un `401` est le jeton, pas l'envoi** : l'entrée reste en attente, et la session est
    // prévenue — c'est elle qui redescend en dégradé et rend le refresh de la passe suivante
    // possible. La classer en échec la condamnerait à rejouer le même jeton mort.
    //
    // La reprise s'espace sur un compteur **à part** d'`attempts` : celui-là porte toute
    // l'histoire de l'entrée — une matinée hors réseau a pu le mener à sept — et lui faire
    // porter la cadence du `401` ferait attendre sept minutes à une entrée dont le refresh
    // vient justement de réparer la session. Or c'est le cas nominal : le jeton expire toutes
    // les quinze minutes (M23). Compté à part, le premier refus repart au tour suivant ; seule
    // une session qu'aucun refresh ne répare — dérive d'horloge, compte révoqué au refresh
    // token encore valide — finit par s'espacer, et la boucle refresh → 401 → média entier sur
    // la 4G ne tourne plus à la minute.
    if (error instanceof ApiError && error.status === 401) {
      options.onUnauthorized?.(accessToken)
      const tokenRefusals = (entry.tokenRefusals ?? 0) + 1

      await updateRow(db.outbox, entry.id, {
        state: 'pending',
        attempts: entry.attempts + 1,
        tokenRefusals,
        lastError: 'Session expirée — reconnexion en cours, l’envoi repartira tout seul.',
        nextAttemptAt: new Date(Date.now() + retryDelayMs(tokenRefusals)).toISOString(),
      })
      return
    }

    // Un refus définitif du serveur (4xx) reste visible avec son motif et ne se rejoue plus
    // tout seul — le même envoi rendrait le même refus. « Réessayer l'envoi » le remet en jeu
    // (M21 — signaler, ne pas effacer). Les exceptions transitoires — `429`, `408`, et le `404`
    // servi par l'infrastructure plutôt que par l'API — sont classées par `isTransient` : une
    // coupure d'infra de trente secondes ne gare pas la file de toute l'équipe, un vrai refus si.
    const definitive =
      error instanceof ApiError &&
      error.status >= 400 &&
      error.status < 500 &&
      !isTransient(error, atDeposit)

    if (definitive) {
      await updateRow(db.outbox, entry.id, {
        state: 'failed',
        attempts: entry.attempts + 1,
        lastError:
          atDeposit && error.status === 413
            ? tooHeavyMessage(entry.submission)
            : refusalMessage(error),
        nextAttemptAt: null,
      })
      return
    }

    // Panne de réseau ou du serveur : repasse en attente et repartira toute seule, de plus en
    // plus espacée (M21 — signaler, ne pas effacer). `tokenRefusals` retombe à zéro : c'est ce
    // qui rend le compteur **consécutif** — sans cette remise, deux expirations de jeton
    // séparées par une matinée hors réseau s'additionneraient, et la promesse « le premier
    // refus repart au tour suivant » ne tiendrait qu'une fois dans la vie de l'entrée.
    const attempts = entry.attempts + 1

    await updateRow(db.outbox, entry.id, {
      state: 'pending',
      attempts,
      tokenRefusals: 0,
      lastError: transientMessage(error),
      nextAttemptAt: new Date(Date.now() + retryDelayMs(attempts)).toISOString(),
    })
  }
}

/**
 * Monte une pièce : une **vidéo** en direct au stockage, par une URL signée redemandée à chaque
 * tentative (#418) ; le reste — et une vidéo quand le serveur ne signe pas — par le relais de
 * l'API. Les octets partent tels quels, sans compression.
 */
async function uploadMedium(api: SyncApi, accessToken: string, medium: LocalMedium): Promise<void> {
  const contentType = medium.blob.type.toLowerCase()

  if (isVideoMediaType(contentType)) {
    const ticket = await api.mediaUploadUrl(accessToken, medium.id, {
      contentType: contentType as MediaUploadRequest['contentType'],
      size: medium.blob.size,
    })

    if (ticket.mode === 'direct') {
      try {
        await (api.putSigned ?? putSigned)(ticket.url, medium.blob, (fraction) => {
          setUploadProgress(medium.id, fraction)
        })
      } finally {
        setUploadProgress(medium.id, null)
      }
      return
    }
  }

  await api.uploadMedia(accessToken, medium.id, medium.blob)
}

/**
 * Ce que l'agent lit d'un refus définitif. Le statut HTTP reste entre parenthèses : c'est ce
 * que le bureau demandera au téléphone, et `POST /sync/inspections → 403` n'est pas une
 * phrase de terrain.
 */
function refusalMessage(error: ApiError): string {
  return `Envoi refusé par le serveur (${error.status}) : il ne sera pas retenté automatiquement. Prévenez le bureau.`
}

/**
 * Le `413` du dépôt tombe avant le contrôleur : il ne dit rien, c'est la tablette qui sait ce qui
 * pèse. Le schéma est nommé parce que c'est le seul contenu dont le poids suit la durée d'un geste
 * (#246) — et la sortie est dite, puisque « Réessayer l'envoi » rejouerait le même corps.
 */
function tooHeavyMessage(submission: InspectionSubmission): string {
  const kib = (value: unknown) =>
    Math.round(new TextEncoder().encode(JSON.stringify(value)).length / 1024)
  const schematic = submission.payload.survey.schematic

  // Un seul chiffre : le schéma pèse souvent presque tout, et « 1111 Kio, dont 1111 Kio » se lit
  // comme une erreur d'affichage.
  return schematic === undefined
    ? `Envoi refusé par le serveur : la fiche pèse ${kib(submission)} Kio, au-delà du mégaoctet accepté. Rouvrez la fiche pour l’alléger, puis transmettez-la de nouveau.`
    : `Envoi refusé par le serveur : la fiche dépasse le mégaoctet accepté, et le schéma dessiné y pèse ${kib(schematic)} Kio. Rouvrez la fiche pour alléger le schéma, puis transmettez-la de nouveau.`
}

function transientMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return `Le serveur n’a pas pu prendre l’envoi (${error.status}) : nouvel essai automatique.`
  }
  return 'Le serveur est injoignable : l’envoi repartira au retour du réseau.'
}

/**
 * Lit le corps d'un 409 de dépôt contre le schéma du domaine, **sans jamais lui faire confiance**.
 * Un corps absent, illisible ou à code inconnu de cette version rend `code: null` : on ne devine
 * pas un motif dont dépendrait la reprise, et surtout on ne classe rien comme transmis.
 */
function readDepositConflict(body: unknown): {
  code: DepositConflictCode | null
  message: string
} {
  const parsed = depositConflictSchema.safeParse(body)
  if (parsed.success) return parsed.data

  return {
    code: null,
    message:
      'Le serveur a signalé un conflit sans motif lisible : le relevé n’est pas considéré comme enregistré. À vérifier avec le bureau.',
  }
}

/**
 * Les médias que **l'enveloppe référence**, confrontés à ce que la tablette porte encore.
 *
 * ⚠️ Partir de la table des médias laisserait les deux listes diverger sans que rien ne le voie :
 * une pièce prise après le verrou serait montée pour rien, et une pièce évincée par le navigateur
 * entre la mise en file et l'envoi manquerait sans que personne ne s'en aperçoive avant la
 * validation. Les octets voyagent à part (D5), le dépôt est le seul point où les deux chemins se
 * voient côté tablette.
 */
async function referencedMedia(
  db: DataDatabase,
  submission: InspectionSubmission,
): Promise<{ present: LocalMedium[]; missing: string[] }> {
  const referenced = new Set(submission.payload.media.map((medium) => medium.id))
  if (submission.payload.signatureMediaId) referenced.add(submission.payload.signatureMediaId)

  const ids = [...referenced]
  // ⚠️ `bulkGet` garde une case par identifiant demandé, `undefined` comprise : le calcul de
  // `missing` ci-dessous est **positionnel**, et un tableau plus court ferait refuser une fiche
  // intacte pour pièces disparues.
  const rows = await db.media.bulkGet([...ids])

  return {
    present: rows.filter((row): row is LocalMedium => row !== undefined),
    missing: ids.filter((_, index) => rows[index] === undefined),
  }
}

async function markSent(db: DataDatabase, entry: OutboxEntry): Promise<void> {
  await updateRow(db.outbox, entry.id, {
    state: 'sent',
    attempts: entry.attempts + 1,
    lastError: null,
    conflictCode: null,
    nextAttemptAt: null,
    sentAt: new Date().toISOString(),
  })
}

function byTransmissionOrder(left: OutboxEntry, right: OutboxEntry): number {
  return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id)
}

async function latestEntry(db: DataDatabase, inquiryId: string): Promise<OutboxEntry | null> {
  return latestOf(await db.outbox.where('inquiryId').equals(inquiryId).toArray())
}

function latestOf(entries: OutboxEntry[]): OutboxEntry | null {
  return [...entries].sort(byTransmissionOrder).at(-1) ?? null
}

/**
 * M21 — l'enveloppe est confrontée au schéma que le serveur applique, **avant le verrou**.
 * Verrouiller puis se faire refuser en `400` rendrait la fiche immuable *et* intransmissible du
 * même geste, à répétition et sans que l'agent puisse rien corriger. Ici rien n'est écrit, et le
 * message nomme le champ.
 */
function checked(submission: InspectionSubmission): InspectionSubmission {
  const parsed = inspectionSubmissionSchema.safeParse(submission)
  if (parsed.success) return submission

  const faults = parsed.error.issues
    .map((issue) => `${issue.path.join('.') || 'la fiche'} : ${issue.message}`)
    .join(' ; ')

  throw new Error(`Cette fiche ne peut pas être transmise en l’état — ${faults}`)
}

/**
 * Le relevé et **le numéro de version qui lui correspond vraiment**.
 *
 * ⚠️ Estampiller `SURVEY_VERSION` sans condition était un mensonge sur le seul brouillon où ça
 * comptait : celui commencé avant la mise à jour de l'app. Il reste sur le disque tel quel, et
 * partait annoncé en v2 avec un `methodsUsed` portant `COMCOM` — une valeur que la montée est
 * justement là pour retirer — sans jamais passer par `upgradeSurvey`. C'est l'inverse en écriture de l'invariant que
 * le dépôt pose : un relevé est **toujours** validé contre le schéma de la version annoncée.
 *
 * ⚠️ Une montée qui échoue **transmet en v1**, elle ne bloque pas et ne ment pas. Le dépôt accepte
 * les deux versions, le serveur relit la v1 — tandis que refuser laisserait la fiche sur la
 * tablette, et l'annoncer en v2 rangerait en base un relevé que rien n'a vérifié (M21).
 */
function surveyOf(
  draft: InspectionDraft,
): { survey: SurveyV2; surveyVersion: 2 } | { survey: SurveyV1; surveyVersion: 1 } {
  if (draft.surveyVersion === SURVEY_VERSION) {
    return { survey: draft.survey, surveyVersion: SURVEY_VERSION }
  }

  const upgraded = upgradeSurvey(draft.survey)

  return upgraded === null
    ? { survey: draft.survey, surveyVersion: SURVEY_V1_VERSION }
    : { survey: upgraded, surveyVersion: SURVEY_VERSION }
}

/**
 * L'enveloppe de dépôt (D4), composée **une fois pour toutes** à la mise en file : un rejeu
 * renvoie exactement ce qui a été transmis, pas une recomposition d'un brouillon entre-temps
 * relu autrement.
 */
function buildSubmission(
  draft: InspectionDraft,
  media: LocalMedium[],
  submittedAt: string,
  vocabulary: readonly VocabularyEntry[],
  contact: AppointmentContact | null,
): InspectionSubmission {
  return {
    idempotencyKey: draft.idempotencyKey,
    inquiryId: draft.inquiryId,
    submittedAt,
    payloadVersion: PROPOSAL_PAYLOAD_VERSION,
    payload: {
      ...surveyOf(draft),
      anomalies: draft.anomalies,
      // `?? null` pour la raison de `performedOn` plus bas : un brouillon ouvert avant ce champ
      // le relit `undefined`. La conclusion **dérivée** ne se substitue jamais à son absence —
      // c'est elle qui dit que l'agent n'a pas surchargé, et le serveur sait la recalculer
      // (`resolveInspectionResult`).
      result: draft.result ?? null,
      // Le média voyage à part (D5) : la fiche n'en porte que la référence.
      // ⚠️ `sourceFingerprint` voyage **avec** le média depuis le 18/09/2026 : sans elle, le
      // bureau ne distingue pas une archive à jour d'une archive périmée, et son avertissement
      // se déclenchait sur tous les contrôles. Elle ne concerne que le type `schematic` ; les
      // autres médias la portent à `null`, ce que le contrat accepte.
      media: media.map(({ id, type, takenAt, label, sourceFingerprint, blob, carriedFrom }) => ({
        id,
        type,
        takenAt,
        label,
        sourceFingerprint: sourceFingerprint ?? null,
        // Le bureau sait ainsi qu'une pièce est une vidéo **sans la télécharger** (#418).
        contentType: blob?.type || null,
        // La provenance d'une pièce reprise de la visite précédente (#675) : c'est elle qui la
        // garde distincte au bureau, au rapport et à l'export.
        carriedFrom: carriedFrom ?? null,
      })),
      metPerson: metPersonOf(draft, contact),
      certifiedWaterPoints: draft.certifiedWaterPoints,
      certifiedNoSepticTank: draft.certifiedNoSepticTank,
      // `false` et non `null` sur un brouillon d'avant la case : la fiche part de l'onglet
      // Transmettre, qui la lui a présentée. `null` reste aux tablettes qui ne la montraient pas.
      consentedDataRetention: draft.consentedDataRetention ?? false,
      signatureMediaId: draft.signatureMediaId,
      signedAt: draft.signedAt,
      // `?? null` et non `draft.performedOn` : un brouillon ouvert avant ce champ le relit
      // `undefined`, que le schéma d'enveloppe ne transporterait pas.
      //
      // ⚠️ **Une date illisible part à `null`, elle ne bloque pas.** C'est le seul champ saisi
      // dont la valeur puisse faire échouer `checked()` : la fiche devenait alors
      // intransmissible, sur un bandeau qui accuse le stockage de la tablette. Le garde-fou 3
      // ne refuse que sur l'intégrité, la sécurité ou un acte irréversible — une date mal
      // saisie n'est aucun des trois, et les deux écrans l'ont déjà signalée comme manquante.
      performedOn: performedOnSchema.safeParse(draft.performedOn).success
        ? (draft.performedOn ?? null)
        : null,
      lat: draft.lat,
      lon: draft.lon,
      notes: draft.notes,
      // Ce que la carte « Travaux à réaliser » montrait à l'agent quand il a transmis : sa saisie,
      // ou la proposition s'il n'y a pas touché. Contrairement à `result`, rien ne la recalcule
      // au bureau, qui imprime ce champ tel quel (Q7). Vidé par l'agent : `null`, aucun travail.
      recommendations: (draft.recommendations ?? suggestRecommendations(draft, vocabulary)) || null,
    },
  }
}

/**
 * Dépose un **avis de passage** dans la file — « nous sommes venus, personne n'a ouvert ».
 *
 * ⚠️ **Le geste doit marcher hors réseau, et c'est sa raison d'être** : l'agent est devant une
 * porte close, souvent au fond d'une impasse, et c'est précisément là que le signal manque. Le
 * constat s'écrit donc en local et part quand il peut, comme un relevé.
 *
 * ⚠️ **Un second appui ne fait pas un second avis tant que le premier n'est pas parti.** Deux avis
 * sur la même demande sont pourtant légitimes — le motif « 2ᵉ passage sans réponse » existe —,
 * mais à deux jours d'intervalle, pas à deux secondes : le doublon accidentel enverrait deux
 * e-mails à la même personne, qui appellerait pour comprendre. La garde ne porte donc que sur ce
 * qui n'a pas encore quitté la tablette.
 *
 * ⚠️ **`noticedAt` est relevé ici**, pas à l'arrivée : dater au serveur citerait dans l'e-mail une
 * heure à laquelle personne n'est passé.
 */
export async function recordPassageNotice(
  db: DataDatabase,
  inquiryId: string,
  reason: PassageNoticeReason,
  noticedAt: string = new Date().toISOString(),
  comment: string | null = null,
): Promise<PassageNoticeEntry> {
  const pending = (await db.passageNotices.where('inquiryId').equals(inquiryId).toArray()).find(
    (entry) => REPLAYABLE.includes(entry.state),
  )

  if (pending !== undefined) return pending

  const entry: PassageNoticeEntry = {
    id: newUuidV7(),
    inquiryId,
    noticedAt,
    reason,
    comment,
    state: 'pending',
    attempts: 0,
    lastError: null,
    createdAt: new Date().toISOString(),
    sentAt: null,
  }
  await db.passageNotices.put(entry)

  return entry
}

/**
 * Une passe d'avis — la même discipline que celle des contrôles, sans les médias.
 *
 * ⚠️ **La clé d'idempotence se compose de la demande et de l'instant du passage**, jamais d'un
 * identifiant tiré au hasard : celui-ci changerait à chaque rejeu et n'écarterait rien. C'est le
 * contrat qui l'exige (`recordPassageNoticeSchema`), et c'est ce qui rend le rejeu inoffensif —
 * une tablette éteinte en pleine tentative reprend sans produire un second e-mail.
 */
async function sendQueuedNotices(
  db: DataDatabase,
  api: PassageNoticeApi,
  getToken: () => string | null,
  options: FlushOptions,
): Promise<void> {
  let entries: PassageNoticeEntry[]

  try {
    entries = await db.passageNotices.where('state').anyOf(REPLAYABLE).toArray()
  } catch (error) {
    console.error('[sync] file des avis de passage illisible, passe abandonnée', error)
    return
  }

  entries.sort(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
  )

  for (const entry of entries) {
    const accessToken = getToken()

    // Session dégradée : rien ne part, rien n'est perdu. Le retour du réseau rappellera.
    if (!accessToken) return

    try {
      await updateRow(db.passageNotices, entry.id, { state: 'sending' })

      await api.record(accessToken, {
        inquiryId: entry.inquiryId,
        noticedAt: entry.noticedAt,
        reason: entry.reason,
        // `?? null` : une ligne écrite avant le champ (Sentry TERRAIN-6) le relit `undefined`.
        comment: entry.comment ?? null,
        idempotencyKey: `${entry.inquiryId}:${entry.noticedAt}`,
      })

      await updateRow(db.passageNotices, entry.id, {
        state: 'sent',
        attempts: entry.attempts + 1,
        lastError: null,
        sentAt: new Date().toISOString(),
      })
    } catch (error) {
      await failNotice(db, entry, error, options, accessToken)
    }
  }
}

/**
 * Le sort d'un avis qui n'est pas parti — **trois issues, et la distinction compte** : un jeton
 * mort se répare tout seul, un refus définitif ne se rejoue plus, un incident réseau attend.
 *
 * ⚠️ **Un avis ne se perd jamais en silence** (M21) : quel que soit le cas, la ligne reste avec
 * le motif, et l'agent la voit. Le constat d'un déplacement ne se reconstitue pas — personne ne
 * peut affirmer après coup qu'on est passé.
 */
async function failNotice(
  db: DataDatabase,
  entry: PassageNoticeEntry,
  error: unknown,
  options: FlushOptions,
  accessToken: string,
): Promise<void> {
  const attempts = entry.attempts + 1

  if (error instanceof ApiError && error.status === 401) {
    // Le jeton **de cet envoi** : `tokenRefused` ne fait rien d'un autre, et la session restait
    // « en ligne » sur un jeton mort, l'avis reprenant un 401 à chaque passe.
    options.onUnauthorized?.(accessToken)

    await updateRow(db.passageNotices, entry.id, {
      state: 'pending',
      attempts,
      lastError: 'Session expirée — reconnexion en cours, l’avis repartira tout seul.',
    })
    return
  }

  // Un refus définitif du serveur reste visible avec son motif : le même envoi rendrait le même
  // refus. Les incidents transitoires — `429`, `408`, une coupure d'infra — restent rejouables,
  // et le `404` se lit **comme au dépôt** (`isTransient`) : sans corps, c'est la page d'erreur d'un
  // déploiement, pas un refus ; l'avis se garait alors pour de bon.
  const definitive =
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    !isTransient(error, true)

  await updateRow(db.passageNotices, entry.id, {
    state: definitive ? 'failed' : 'pending',
    attempts,
    lastError: definitive
      ? `L’avis de passage a été refusé par le serveur (${String(error instanceof ApiError ? error.status : '—')}). Il reste sur la tablette.`
      : 'L’avis de passage n’est pas encore parti — il repartira à la prochaine connexion.',
  })
}

/** Ce que l'agent a fixé avec la personne : le créneau, et qui il a eu en face de lui. */
export interface OnSiteAppointment {
  /** L'instant du rendez-vous, fuseau compris (`parisInstant`). */
  scheduledAt: string
  contactName: string | null
  contactPhone: string | null
  /** Le commentaire de la fenêtre (frame 2.4), `null` quand il est laissé vide. */
  comment: string | null
}

/**
 * Pose dans la file le **rendez-vous pris sur place**, et le montre tout de suite sur la tournée.
 *
 * ⚠️ **Hors réseau aussi** (#268) : l'agent prend le rendez-vous avec la personne sur le pas de la
 * porte, souvent là où le signal manque. L'agenda et la pré-fiche le montrent dès cet instant
 * (`showFieldAppointment`), et il part quand il peut.
 *
 * ⚠️ **L'`id` se tire ici, une fois** : c'est l'identifiant du rendez-vous côté serveur, et le
 * renvoyer à chaque tentative est ce qui rend le rejeu inoffensif.
 */
export async function recordAppointment(
  db: DataDatabase,
  inquiryId: string,
  appointment: OnSiteAppointment,
): Promise<FieldAppointmentEntry> {
  const entry: FieldAppointmentEntry = {
    id: newUuidV7(),
    inquiryId,
    ...appointment,
    state: 'pending',
    attempts: 0,
    lastError: null,
    createdAt: new Date().toISOString(),
    sentAt: null,
  }

  await db.transaction('rw', db.appointments, db.inquiries, async () => {
    await db.appointments.put(entry)
    await showFieldAppointment(db, entry)
  })

  return entry
}

/** Une passe de rendez-vous — la même discipline que celle des avis de passage. */
async function sendQueuedAppointments(
  db: DataDatabase,
  api: FieldAppointmentApi,
  getToken: () => string | null,
  options: FlushOptions,
): Promise<void> {
  let entries: FieldAppointmentEntry[]

  try {
    entries = await db.appointments.where('state').anyOf(REPLAYABLE).toArray()
  } catch (error) {
    console.error('[sync] file des rendez-vous illisible, passe abandonnée', error)
    return
  }

  entries.sort(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
  )

  for (const entry of entries) {
    const accessToken = getToken()

    if (!accessToken) return

    try {
      await updateRow(db.appointments, entry.id, { state: 'sending' })

      await api.recordFieldAppointment(accessToken, {
        id: entry.id,
        inquiryId: entry.inquiryId,
        scheduledAt: entry.scheduledAt,
        contactName: entry.contactName,
        contactPhone: entry.contactPhone,
        // Absent d'une entrée posée avant le 08/10/2026.
        comment: entry.comment ?? null,
      })

      await updateRow(db.appointments, entry.id, {
        state: 'sent',
        attempts: entry.attempts + 1,
        lastError: null,
        sentAt: new Date().toISOString(),
      })
    } catch (error) {
      await failAppointment(db, entry, error, options, accessToken)
    }
  }
}

/**
 * Le sort d'un rendez-vous qui n'est pas parti — les trois issues de {@link failNotice}, et la
 * même règle : la ligne reste, avec son motif (M21).
 */
async function failAppointment(
  db: DataDatabase,
  entry: FieldAppointmentEntry,
  error: unknown,
  options: FlushOptions,
  accessToken: string,
): Promise<void> {
  const attempts = entry.attempts + 1

  if (error instanceof ApiError && error.status === 401) {
    options.onUnauthorized?.(accessToken)

    await updateRow(db.appointments, entry.id, {
      state: 'pending',
      attempts,
      lastError: 'Session expirée — reconnexion en cours, le rendez-vous repartira tout seul.',
    })
    return
  }

  const definitive =
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    !isTransient(error, true)

  await updateRow(db.appointments, entry.id, {
    state: definitive ? 'failed' : 'pending',
    attempts,
    lastError: definitive
      ? `Le rendez-vous a été refusé par le serveur (${String(error instanceof ApiError ? error.status : '—')}). Il reste sur la tablette.`
      : 'Le rendez-vous n’est pas encore parti — il repartira à la prochaine connexion.',
  })
}

/**
 * Pose dans la file une **étape du contrôle** (M42) — `signed` depuis « Valider la signature ».
 * `started` se pose à la naissance du brouillon (`createDraft`). Hors réseau, elle attend et part
 * au retour du réseau, avec l'heure de la tablette.
 *
 * ⚠️ **Rien pour un contrôle libre** (M41) : le serveur n'a pas de demande sous son identifiant,
 * il refusait l'étape (404) et ce refus ne se montrait nulle part.
 */
export async function recordInspectionStep(
  db: DataDatabase,
  inquiryId: string,
  step: FieldInspectionStep,
  occurredAt?: string,
): Promise<void> {
  if ((await db.inquiries.get(inquiryId))?.freeInspection !== undefined) return

  const entry: InspectionStepEntry = {
    ...newInspectionStep(inquiryId, step, occurredAt),
    state: 'pending',
    attempts: 0,
    lastError: null,
    createdAt: new Date().toISOString(),
    sentAt: null,
  }

  await db.inspectionSteps.put(entry)
}

/**
 * Une passe d'étapes — la discipline des avis de passage. L'`id` voyage tel quel : c'est lui que
 * le serveur relit pour qu'un rejeu n'en fasse pas deux.
 */
async function sendQueuedSteps(
  db: DataDatabase,
  api: InspectionStepApi,
  getToken: () => string | null,
  options: FlushOptions,
): Promise<void> {
  let entries: InspectionStepEntry[]

  try {
    entries = await db.inspectionSteps.where('state').anyOf(REPLAYABLE).toArray()
  } catch (error) {
    console.error('[sync] file des étapes illisible, passe abandonnée', error)
    return
  }

  entries.sort(
    (left, right) =>
      left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
  )

  for (const entry of entries) {
    const accessToken = getToken()

    if (!accessToken) return

    try {
      await updateRow(db.inspectionSteps, entry.id, { state: 'sending' })

      await api.recordInspectionStep(accessToken, {
        id: entry.id,
        inquiryId: entry.inquiryId,
        step: entry.step,
        occurredAt: entry.occurredAt,
      })

      await updateRow(db.inspectionSteps, entry.id, {
        state: 'sent',
        attempts: entry.attempts + 1,
        lastError: null,
        sentAt: new Date().toISOString(),
      })
    } catch (error) {
      await failStep(db, entry, error, options, accessToken)
    }
  }
}

/** Les trois issues de {@link failNotice} : jeton mort, refus définitif, incident qui attend. */
async function failStep(
  db: DataDatabase,
  entry: InspectionStepEntry,
  error: unknown,
  options: FlushOptions,
  accessToken: string,
): Promise<void> {
  const attempts = entry.attempts + 1

  if (error instanceof ApiError && error.status === 401) {
    options.onUnauthorized?.(accessToken)
    await updateRow(db.inspectionSteps, entry.id, { state: 'pending', attempts, lastError: null })
    return
  }

  const definitive =
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    !isTransient(error, true)

  await updateRow(db.inspectionSteps, entry.id, {
    state: definitive ? 'failed' : 'pending',
    attempts,
    lastError: definitive
      ? `Étape refusée par le serveur (${String(error instanceof ApiError ? error.status : '—')}).`
      : 'Étape pas encore partie.',
  })
}
